import { zodResolver } from '@hookform/resolvers/zod';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import type { FC } from 'react';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { MuiFormInput, MuiFormSelect } from 'components/Form';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import {
  FlexContainerControlsStyled,
  FormStyled,
  getPopupContentStyle,
} from './AddAttachmentPopup.styled';
import type { TicketAttachmentFormValuesT } from './declarations';
import useAddAttachmentPopup from './hooks/useAddAttachmentPopup';
import validationSchema from './validationSchema';

export type AddAttachmentPopupPropsT = {
  ticketId: string;
  categoryName: string;
  isOpen: boolean;
  assignToWarrCloud: boolean;
  onClose: NoopT;
};

const AddAttachmentPopup: FC<AddAttachmentPopupPropsT> = ({
  ticketId,
  categoryName,
  isOpen,
  assignToWarrCloud,
  onClose,
}) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const {
    handleSubmitForm,
    isCreatingAttachment,
    handleClosePopup,
    isLoadingAttachmentTypesOptions,
    attachmentTypesOptions,
  } = useAddAttachmentPopup({ ticketId, categoryName, assignToWarrCloud });
  const fromMethods = useForm<TicketAttachmentFormValuesT>({
    defaultValues: {
      note: '',
      attachmentTypeCode: '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  /**
   * Variables
   */
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = fromMethods;
  const attachmentFileValue = useWatch<TicketAttachmentFormValuesT, 'attachmentFile'>({
    control,
    name: 'attachmentFile',
  });
  const attachmentPhotoValue = useWatch<TicketAttachmentFormValuesT, 'attachmentPhoto'>({
    control,
    name: 'attachmentPhoto',
  });
  const attachmentFileNames = attachmentFileValue
    ? Array.from(attachmentFileValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentPhotoNames = attachmentPhotoValue
    ? Array.from(attachmentPhotoValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentNames = [attachmentFileNames, attachmentPhotoNames]
    .filter(name => name !== '')
    .join(',');

  /**
   * Effects
   */
  useEffect(
    () => () => {
      if (isOpen) {
        reset();
      }
    },
    [isOpen, reset]
  );

  const attachmentTypesMuiOptions = attachmentTypesOptions.map(o => ({
    value: o.value,
    label: o.label,
  }));

  return (
    <Popup
      popupId={ScrollLockIdE.AddAttachmentPopup}
      modalTitle="Add Attachment"
      contentStyle={getPopupContentStyle(isMobile)}
      size={ModalSizeE.ADD_ATTACHMENT}
      isOpen={isOpen}
      onClose={handleClosePopup(onClose)}
      showCloseBtn
      closeOnEscape={false}
      validationErrorCount={Object.keys(errors).length}
    >
      <FormProvider {...fromMethods}>
        <FormStyled
          role="form"
          onSubmit={handleSubmit((data): void => {
            handleSubmitForm(data, onClose);
          })}
        >
          <Box sx={{ mb: 2 }}>
            <MuiFormInput name="note" label="Attachment Note" placeholder="Attachment Note" />
          </Box>

          <Box sx={{ mb: 2 }} data-testid="add-attachment-type-select">
            <MuiFormSelect
              name="attachmentTypeCode"
              label="Type"
              placeholder=""
              options={attachmentTypesMuiOptions}
              isLoading={isLoadingAttachmentTypesOptions}
              required
            />
          </Box>

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="label" htmlFor="add-attachment-file-input" sx={{ cursor: 'pointer' }}>
              <input
                {...fromMethods.register('attachmentFile')}
                type="file"
                multiple
                id="add-attachment-file-input"
                data-testid="add-attachment-file-input"
                style={{ display: 'none' }}
              />
              <Tooltip title="Choose file" arrow>
                <IconButton
                  component="span"
                  aria-label="Choose file"
                  sx={{
                    color: attachmentFileNames
                      ? (t): string => t.palette.success.main
                      : (t): string => t.palette.primary.main,
                  }}
                >
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {isMobile && (
              <Box
                component="label"
                htmlFor="add-attachment-photo-input"
                sx={{ cursor: 'pointer' }}
              >
                <input
                  {...fromMethods.register('attachmentPhoto')}
                  type="file"
                  capture="user"
                  accept="image/*"
                  multiple
                  id="add-attachment-photo-input"
                  data-testid="add-attachment-photo-input"
                  style={{ display: 'none' }}
                />
                <Tooltip title="Camera" arrow>
                  <IconButton
                    component="span"
                    aria-label="Camera"
                    sx={{
                      color: attachmentPhotoNames
                        ? (t): string => t.palette.success.main
                        : (t): string => t.palette.primary.main,
                    }}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {attachmentNames || 'Attachment not selected'}
            </Typography>
          </Box>
          {!isValid && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {errors.attachmentFile?.message || errors.attachmentPhoto?.message}
            </Typography>
          )}
          <FlexContainerControlsStyled justify={isMobile ? 'center' : 'flex-end'}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isCreatingAttachment}
              startIcon={
                isCreatingAttachment ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              Publish
            </Button>
          </FlexContainerControlsStyled>
        </FormStyled>
      </FormProvider>
    </Popup>
  );
};

export default AddAttachmentPopup;
